import { CommandItem } from '@heidi/design-system/command';
import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import * as React from 'react';
import { useIntl } from 'react-intl';

import type { EhrPatientSearchResultView } from './ehr-patient-search-result';
import { ehrRowValue } from './ehr-row-value';
import { patientResultRowMessages } from './patient-result-row.messages';

export interface EhrPatientResultRowProps {
  result: EhrPatientSearchResultView;
  onSelect: (result: EhrPatientSearchResultView) => void;
}

/**
 * A single external-EHR patient row in the find-or-link dropdown.
 * Mirrors `PatientResultRow`'s layout so the two lists read as one dropdown;
 * the section heading (EHR logo + name) is what differentiates provenance.
 *
 * `result` is the prop contract, not the producer's wire type: the composition
 * point maps the wire value through
 * `src/features/patient-find-or-link/adapters/to-ehr-patient-search-result-view.ts`
 * before it reaches here.
 *
 * `onSelect` therefore hands back the VIEW, and a caller that needs the wire row
 * closes over its own value rather than reading it off this callback. That is
 * what keeps the row un-parameterised: a version generic over the caller's row
 * type would be relying on the wire type being assignable to this one, which is
 * the mechanism the adapter replaces.
 */
export function EhrPatientResultRow({
  result,
  onSelect,
}: EhrPatientResultRowProps): React.ReactElement {
  const intl = useIntl();

  const fullName =
    [result.first_name, result.last_name].filter(Boolean).join(' ') ||
    intl.formatMessage(patientResultRowMessages.unnamedPatient);

  const secondaryParts: string[] = [];
  if (result.nhs_number) {
    secondaryParts.push(
      intl.formatMessage(
        {
          defaultMessage: 'NHS {nhsNumber}',
          id: 'GY5q7B+mfZ',
          description: 'NHS number shown for an external EHR patient search result',
        },
        { nhsNumber: result.nhs_number }
      )
    );
  }
  if (result.dob) {
    // Date-only ISO (`YYYY-MM-DD`) parses as UTC midnight; format in UTC so
    // negative-offset locales don't show the previous calendar day.
    const dob = new Date(result.dob);
    if (!Number.isNaN(dob.getTime())) {
      secondaryParts.push(
        intl.formatMessage(
          {
            defaultMessage: 'Born {dob}',
            id: 'TYex0G1AFK',
            description: 'Date of birth shown for an external EHR patient search result',
          },
          {
            dob: intl.formatDate(dob, {
              dateStyle: 'medium',
              timeZone: 'UTC',
            }),
          }
        )
      );
    }
  }

  return (
    <CommandItem
      value={ehrRowValue(result)}
      onSelect={() => onSelect(result)}
      className={cn(
        'flex flex-col items-start gap-y-0.5 px-3 py-2.5',
        'data-[selected=true]:bg-surface-pressed'
      )}
      data-testid="ehr-patient-result-row"
    >
      <TypographyP2Bold className="truncate text-text-primary">
        {fullName}
      </TypographyP2Bold>
      {secondaryParts.length > 0 && (
        <TypographyCaption className="truncate text-text-secondary">
          {secondaryParts.join(' · ')}
        </TypographyCaption>
      )}
    </CommandItem>
  );
}
